// The destinations' routes, worked out from their registries (src/modules/*.tsx). App.tsx puts these inside the
// signed-in layout; nothing here is edited by hand when a page is added or moved.
import { Fragment, Suspense, type ReactNode } from "react";
import { Navigate, Route } from "react-router-dom";
import { ComingSoon } from "../components/module";
import { Loading } from "../components/ui";
import { MODULE_DEFS } from ".";
import { pagePath, stepPath, type ModuleDef, type ModulePage, type ModuleRoute } from "./types";

const rel = (path: string) => path.replace(/^\//, "");

/** Registry screens load when first opened (modules/screen.ts). */
export const opened = (node: ReactNode) => <Suspense fallback={<Loading label="Opening the page" />}>{node}</Suspense>;

/** A registry page's screen, as its route shows it. */
export function pageElement(p: ModulePage) {
  return p.render ? opened(p.render()) : <ComingSoon title={p.label} what={p.comingSoon} step={p.step} />;
}

function flatRoutes(list: ModuleRoute[] | undefined) {
  return (list ?? []).map((r) => <Route key={r.path} path={rel(r.path)} element={opened(r.render())} />);
}

/** Every route of one destination: its pages, each tab's and the destination's landing, and extra pages. */
export function moduleRoutes(m: ModuleDef) {
  // A module switched off for now: every address under it says Coming soon (old links still land).
  if (m.paused) {
    return <Route key={m.key} path={`${m.key}/*`} element={<ComingSoon title={m.label} what={m.paused} />} />;
  }
  const own =
    m.layout === "flat"
      ? flatRoutes(m.items)
      : m.tabs.map((tab) => (
          <Fragment key={tab.key}>
            {/* A tab whose first page has its own slug: /attract/actions opens that page. */}
            {tab.pages[0]!.slug !== "" && <Route path={rel(stepPath(m.key, tab.key))} element={<Navigate to={pagePath(m.key, tab.key, tab.pages[0]!.slug)} replace />} />}
            {tab.pages.map((p) => (
              <Fragment key={p.slug}>
                <Route path={rel(pagePath(m.key, tab.key, p.slug))} element={pageElement(p)} />
                {(p.routes ?? []).map((sub) => (
                  <Route key={sub.path} path={`${rel(pagePath(m.key, tab.key, p.slug))}/${sub.path}`} element={opened(sub.render())} />
                ))}
              </Fragment>
            ))}
          </Fragment>
        ));
  const first = m.layout === "steps" ? pagePath(m.key, m.tabs[0]!.key, m.tabs[0]!.pages[0]!.slug) : null;
  return (
    <Fragment key={m.key}>
      {/* /prospects opens the first tab's first page, or where the destination's own landing sends it */}
      {first && <Route path={m.key} element={m.layout === "steps" && m.landing ? opened(m.landing()) : <Navigate to={first} replace />} />}
      {own}
      {flatRoutes(m.extraPages)}
    </Fragment>
  );
}

/** The registry page at an address like /settings/sending/times, or null. */
export function pageAt(path: string): ModulePage | null {
  for (const m of MODULE_DEFS) {
    if (m.layout !== "steps") continue;
    for (const tab of m.tabs) for (const p of tab.pages) if (pagePath(m.key, tab.key, p.slug) === path) return p;
  }
  return null;
}

export function allModuleRoutes() {
  return MODULE_DEFS.map(moduleRoutes);
}

/** Every address the destinations answer, as route patterns (for the link check and the nav tests). */
export function modulePatterns(): string[] {
  const out: string[] = [];
  for (const m of MODULE_DEFS) {
    if (m.layout === "flat") out.push(...m.items.map((i) => i.path));
    else {
      out.push(`/${m.key}`);
      for (const tab of m.tabs) {
        out.push(stepPath(m.key, tab.key));
        for (const p of tab.pages) {
          out.push(pagePath(m.key, tab.key, p.slug));
          for (const sub of p.routes ?? []) out.push(`${pagePath(m.key, tab.key, p.slug)}/${sub.path}`);
        }
      }
    }
    out.push(...(m.extraPages ?? []).map((r) => r.path));
  }
  return [...new Set(out)];
}
